import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Alert from '../../../components/common/Alert';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ResignationFormModal from './ResignationFormModal';
import ExitForm from './ExitForm';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;

const editableFields = [
  'first_name', 'last_name', 'date_of_birth', 'father_name', 'mother_name',
  'phone_number', 'adhaar_number', 'pan_number', 'emergency_number'
];

// --- Education Modal ---
export function EducationModal({ onClose, onSave }) {
  const [form, setForm] = React.useState({ education_type: '', college_name: '', college_location: '', start_year: '', end_year: '' });
  const [saving, setSaving] = React.useState(false);
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    await onSave(form, () => setForm({ education_type: '', college_name: '', college_location: '', start_year: '', end_year: '' }));
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <form onSubmit={handleSubmit} className="bg-white rounded shadow-lg p-6 w-full max-w-md">
        <h4 className="text-lg font-bold mb-4">Add Education</h4>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
            <input name="education_type" value={form.education_type} onChange={handleChange} required className="w-full px-2 py-1 border rounded bg-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">College Name</label>
            <input name="college_name" value={form.college_name} onChange={handleChange} required className="w-full px-2 py-1 border rounded bg-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Location</label>
            <input name="college_location" value={form.college_location} onChange={handleChange} required className="w-full px-2 py-1 border rounded bg-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Start Year</label>
            <input type="date" name="start_year" value={form.start_year} onChange={handleChange} required className="w-full px-2 py-1 border rounded bg-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">End Year</label>
            <input type="date" name="end_year" value={form.end_year} onChange={handleChange} required className="w-full px-2 py-1 border rounded bg-gray-100" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="bg-gray-200 text-gray-800 px-4 py-2 rounded">Cancel</button>
          <button type="submit" disabled={saving} className="bg-indigo-600 text-white px-4 py-2 rounded">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

// --- Attachment Modal ---
export function AttachmentModal({ onClose, onSave }) {
  const [form, setForm] = React.useState({ document_type: '', files: [] });
  const [saving, setSaving] = React.useState(false);
  const handleChange = e => {
    if (e.target.name === 'files') {
      setForm(f => ({ ...f, files: Array.from(e.target.files) }));
    } else {
      setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    }
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    await onSave(form, () => setForm({ document_type: '', files: [] }));
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <form onSubmit={handleSubmit} className="bg-white rounded shadow-lg p-6 w-full max-w-md">
        <h4 className="text-lg font-bold mb-4">Add Attachment</h4>
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Document Type</label>
          <input name="document_type" value={form.document_type} onChange={handleChange} required className="w-full px-2 py-1 border rounded bg-gray-100" />
        </div>
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Files</label>
          <input type="file" name="files" multiple onChange={handleChange} className="w-full" />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="bg-gray-200 text-gray-800 px-4 py-2 rounded">Cancel</button>
          <button type="submit" disabled={saving} className="bg-indigo-600 text-white px-4 py-2 rounded">{saving ? 'Uploading...' : 'Add'}</button>
        </div>
      </form>
    </div>
  );
}

// --- Asset Modal ---
export function AssetModal({ onClose, onSave, editingAsset }) {
  const [form, setForm] = React.useState({
    asset_name: '',
    serial_no: '',
    allotted_date: '',
    surrender_date: ''
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (editingAsset) {
      setForm({
        asset_name: editingAsset.asset_name,
        serial_no: editingAsset.serial_no,
        allotted_date: editingAsset.allotted_date,
        surrender_date: editingAsset.surrender_date || ''
      });
    }
  }, [editingAsset]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setError(''); // Clear error when user makes changes
  };
  
  const validateDates = () => {
    if (form.surrender_date && form.allotted_date) {
      const allotted = new Date(form.allotted_date);
      const surrender = new Date(form.surrender_date);
      if (surrender < allotted) {
        setError('Surrender date cannot be before allotted date');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateDates()) return;
    
    setSaving(true);
    // Convert empty surrender date to null before sending to API
    const formData = {
      ...form,
      surrender_date: form.surrender_date || null
    };
    await onSave(formData, () => setForm({
      asset_name: '',
      serial_no: '',
      allotted_date: '',
      surrender_date: ''
    }));
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <form onSubmit={handleSubmit} className="bg-white rounded shadow-lg p-6 w-full max-w-md">
        <h4 className="text-lg font-bold mb-4">{editingAsset ? 'Edit Asset' : 'Add Asset'}</h4>
        {error && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 text-red-600 rounded text-sm">
            {error}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Asset Name</label>
            <input name="asset_name" value={form.asset_name} onChange={handleChange} required className="w-full px-2 py-1 border rounded bg-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Serial No./Model</label>
            <input name="serial_no" value={form.serial_no} onChange={handleChange} required className="w-full px-2 py-1 border rounded bg-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Allotted Date</label>
            <input 
              type="date" 
              name="allotted_date" 
              value={form.allotted_date} 
              onChange={handleChange} 
              required 
              className="w-full px-2 py-1 border rounded bg-gray-100" 
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Surrender Date
              <span className="text-gray-400 ml-1">(Optional)</span>
            </label>
            <input 
              type="date" 
              name="surrender_date" 
              value={form.surrender_date} 
              onChange={handleChange}
              min={form.allotted_date} // Set minimum date to allotted date
              className="w-full px-2 py-1 border rounded bg-gray-100" 
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="bg-gray-200 text-gray-800 px-4 py-2 rounded">Cancel</button>
          <button type="submit" disabled={saving} className="bg-indigo-600 text-white px-4 py-2 rounded">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

const Profile = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEduModal, setShowEduModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: null
  });
  const [resignationStatus, setResignationStatus] = useState(null);
  const [isResignationModalOpen, setIsResignationModalOpen] = useState(false);
  const [isExitFormOpen, setIsExitFormOpen] = useState(false);

  // Get current user info from localStorage
  const currentUserId = localStorage.getItem('user_id');
  const currentUserRole = localStorage.getItem('role_id');

  // Determine if we're viewing own profile or another employee's profile
  const isOwnProfile = !employeeId; //If there is No employeeId, then it is own profile
  const targetUserId = employeeId || currentUserId;

  // Determine if current user can edit
  const canEdit = isOwnProfile || 
    currentUserRole === '1' || // Admin
    (currentUserRole === '3' && String(user?.employee?.reporting_manager_id) === String(currentUserId)); // His Reporting Manager

  // Add Education
  const handleAddEducation = async (eduForm, resetForm) => {
    try {
      const res = await fetch(`${API_BASE}/api/accounts/user/${targetUserId}/education/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(eduForm)
      });
      if (!res.ok) throw new Error('Failed to add education');
      toast.success('Education added');
      
      // Fetch updated profile data
      const endpoint = isOwnProfile 
        ? `${API_BASE}/api/accounts/user/${targetUserId}/`
        : `${API_BASE}/api/accounts/employee/${targetUserId}/profile/`;
        
      const profileRes = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (!profileRes.ok) throw new Error('Failed to fetch updated profile');
      const data = await profileRes.json();
      
      if (isOwnProfile) {
        setUser(data);
        setForm({ ...data.employee });
      } else {
        setUser({
          ...data,
          employee: data,
          email: data.email,
          role_name: data.role_name
        });
        setForm({ ...data });
      }
      
      setShowEduModal(false);
      if (resetForm) resetForm();
    } catch (e) {
      toast.error(e.message || 'Failed to add education');
    }
  };

  // Delete Education
  const handleDeleteEducation = async (eduId) => {
    setAlertConfig({
      isOpen: true,
      title: 'Delete Education',
      message: 'Are you sure you want to delete this education record? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await fetch(`${API_BASE}/api/accounts/user/${targetUserId}/education/${eduId}/`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          if (!res.ok) throw new Error('Failed to delete education');
          toast.success('Education deleted');
          
          // Fetch updated profile data
          const endpoint = isOwnProfile 
            ? `${API_BASE}/api/accounts/user/${targetUserId}/`
            : `${API_BASE}/api/accounts/employee/${targetUserId}/profile/`;
            
          const profileRes = await fetch(endpoint, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          });
          if (!profileRes.ok) throw new Error('Failed to fetch updated profile');
          const data = await profileRes.json();
          
          if (isOwnProfile) {
            setUser(data);
            setForm({ ...data.employee });
          } else {
            setUser({
              ...data,
              employee: data,
              email: data.email,
              role_name: data.role_name
            });
            setForm({ ...data });
          }
        } catch (e) {
          toast.error(e.message || 'Failed to delete education');
        } finally {
          setAlertConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  // Add Attachment
  const handleAddAttachment = async (attachForm, resetForm) => {
    try {
      const formData = new FormData();
      formData.append('document_type', attachForm.document_type);
      (attachForm.files || []).forEach(file => formData.append('files', file));
      const res = await fetch(`${API_BASE}/api/accounts/user/${targetUserId}/attachment/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      if (!res.ok) throw new Error('Failed to add attachment');
      toast.success('Attachment added');
      
      // Fetch updated profile data
      const endpoint = isOwnProfile 
        ? `${API_BASE}/api/accounts/user/${targetUserId}/`
        : `${API_BASE}/api/accounts/employee/${targetUserId}/profile/`;
        
      const profileRes = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (!profileRes.ok) throw new Error('Failed to fetch updated profile');
      const data = await profileRes.json();
      
      if (isOwnProfile) {
        setUser(data);
        setForm({ ...data.employee });
      } else {
        setUser({
          ...data,
          employee: data,
          email: data.email,
          role_name: data.role_name
        });
        setForm({ ...data });
      }
      
      setShowAttachModal(false);
      if (resetForm) resetForm();
    } catch (e) {
      toast.error(e.message || 'Failed to add attachment');
    }
  };

  // Delete Attachment
  const handleDeleteAttachment = async (attId) => {
    setAlertConfig({
      isOpen: true,
      title: 'Delete Attachment',
      message: 'Are you sure you want to delete this attachment? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await fetch(`${API_BASE}/api/accounts/user/${targetUserId}/attachment/${attId}/`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          if (!res.ok) throw new Error('Failed to delete attachment');
          toast.success('Attachment deleted');
          
          // Fetch updated profile data
          const endpoint = isOwnProfile 
            ? `${API_BASE}/api/accounts/user/${targetUserId}/`
            : `${API_BASE}/api/accounts/employee/${targetUserId}/profile/`;
            
          const profileRes = await fetch(endpoint, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          });
          if (!profileRes.ok) throw new Error('Failed to fetch updated profile');
          const data = await profileRes.json();
          
          if (isOwnProfile) {
            setUser(data);
            setForm({ ...data.employee });
          } else {
            setUser({
              ...data,
              employee: data,
              email: data.email,
              role_name: data.role_name
            });
            setForm({ ...data });
          }
        } catch (e) {
          toast.error(e.message || 'Failed to delete attachment');
        } finally {
          setAlertConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  // Add Asset
  const handleAddAsset = async (assetForm, resetForm) => {
    try {
      const res = await fetch(`${API_BASE}/api/accounts/user/${targetUserId}/asset/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(assetForm)
      });
      if (!res.ok) throw new Error('Failed to add asset');
      toast.success('Asset added');
      
      // Fetch updated profile data
      const endpoint = isOwnProfile 
        ? `${API_BASE}/api/accounts/user/${targetUserId}/`
        : `${API_BASE}/api/accounts/employee/${targetUserId}/profile/`;
        
      const profileRes = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (!profileRes.ok) throw new Error('Failed to fetch updated profile');
      const data = await profileRes.json();
      
      if (isOwnProfile) {
        setUser(data);
        setForm({ ...data.employee });
      } else {
        setUser({
          ...data,
          employee: data,
          email: data.email,
          role_name: data.role_name
        });
        setForm({ ...data });
      }
      
      setShowAssetModal(false);
      if (resetForm) resetForm();
    } catch (e) {
      toast.error(e.message || 'Failed to add asset');
    }
  };

  // Edit Asset
  const handleEditAsset = async (assetForm, resetForm) => {
    try {
      const res = await fetch(`${API_BASE}/api/accounts/user/${targetUserId}/asset/${editingAsset.id}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(assetForm)
      });
      if (!res.ok) throw new Error('Failed to update asset');
      toast.success('Asset updated');
      
      // Fetch updated profile data
      const endpoint = isOwnProfile 
        ? `${API_BASE}/api/accounts/user/${targetUserId}/`
        : `${API_BASE}/api/accounts/employee/${targetUserId}/profile/`;
        
      const profileRes = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (!profileRes.ok) throw new Error('Failed to fetch updated profile');
      const data = await profileRes.json();
      
      if (isOwnProfile) {
        setUser(data);
        setForm({ ...data.employee });
      } else {
        setUser({
          ...data,
          employee: data,
          email: data.email,
          role_name: data.role_name
        });
        setForm({ ...data });
      }
      
      setShowAssetModal(false);
      setEditingAsset(null);
      if (resetForm) resetForm();
    } catch (e) {
      toast.error(e.message || 'Failed to update asset');
    }
  };

  // Delete Asset
  const handleDeleteAsset = async (assetId) => {
    setAlertConfig({
      isOpen: true,
      title: 'Delete Asset',
      message: 'Are you sure you want to delete this asset record? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await fetch(`${API_BASE}/api/accounts/user/${targetUserId}/asset/${assetId}/`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          if (!res.ok) throw new Error('Failed to delete asset');
          toast.success('Asset deleted');
          
          // Fetch updated profile data
          const endpoint = isOwnProfile 
            ? `${API_BASE}/api/accounts/user/${targetUserId}/`
            : `${API_BASE}/api/accounts/employee/${targetUserId}/profile/`;
            
          const profileRes = await fetch(endpoint, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          });
          if (!profileRes.ok) throw new Error('Failed to fetch updated profile');
          const data = await profileRes.json();
          
          if (isOwnProfile) {
            setUser(data);
            setForm({ ...data.employee });
          } else {
            setUser({
              ...data,
              employee: data,
              email: data.email,
              role_name: data.role_name
            });
            setForm({ ...data });
          }
        } catch (e) {
          toast.error(e.message || 'Failed to delete asset');
        } finally {
          setAlertConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  useEffect(() => {
    async function fetchProfile() {
      setLoading(true);
      try {
        const endpoint = isOwnProfile 
          ? `${API_BASE}/api/accounts/user/${targetUserId}/`
          : `${API_BASE}/api/accounts/employee/${targetUserId}/profile/`;
          
        const res = await fetch(endpoint, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (!res.ok) throw new Error('Failed to fetch profile');
        const data = await res.json();
        
        // Handle different data structures for own profile vs employee profile
        if (isOwnProfile) {
          setUser(data);
          setForm({ ...data.employee });
        } else {
          // For employee profile, the data is already in the correct structure
          setUser({
            ...data,
            employee: data,
            email: data.email,
            role_name: data.role_name
          });
          setForm({ ...data });
        }
      } catch (e) {
        toast.error(e.message || 'Failed to fetch user profile');
        if (!isOwnProfile) {
          navigate('/leavetracker/team');
        }
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, [targetUserId, isOwnProfile, navigate]);

  useEffect(() => {
    const checkResignationStatus = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/employee/resignation/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        if (response.data && response.data.length > 0) {
          const latestResignation = response.data[0];
          setResignationStatus(latestResignation.status);
        }
      } catch (error) {
        console.error('Error checking resignation status:', error);
      }
    };
    checkResignationStatus();
  }, []);

  const handleResignation = async (formData) => {
    try {
      const response = await axios.post(`${API_BASE}/api/employee/resignation/`, {
        ...formData,
        employee_signature: 'Digital Signature' // You might want to add a signature pad
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('Resignation request submitted successfully');
      setResignationStatus('PENDING');
      setIsResignationModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error submitting resignation request');
    }
  };

  const handleExitForm = () => {
    setIsExitFormOpen(true);
  };

  const handleCloseExitForm = () => {
    setIsExitFormOpen(false);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleEdit = () => setEditMode(true);
  const handleCancel = () => {
    setForm({ ...user.employee });
    setEditMode(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {};
      editableFields.forEach(f => { payload[f] = form[f] || ''; });
      const res = await fetch(`${API_BASE}/api/accounts/user/${targetUserId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to update profile');
      toast.success('Profile updated successfully');
      setEditMode(false);
      // Refresh profile data
      const updated = await res.json();
      setUser(u => ({ ...u, employee: { ...u.employee, ...payload } }));
    } catch (e) {
      toast.error(e.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !user) return <div className="py-8 text-center">Loading...</div>;
  if (!user) return null;

  const e = user.employee || user; // Use employee data or user data if employee is not present

  return (
    <div className="bg-white rounded shadow p-6">
      {!isOwnProfile && (
        <div className="flex justify-end mb-4">
          <button 
            onClick={() => navigate('/leavetracker/team')}
            className="bg-white text-blue-600 px-4 py-2 rounded-lg border border-blue-600 hover:bg-blue-50 transition-colors"
          >
            Back to Team
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">User Profile</h2>
        {canEdit && (editMode ? (
          <div className="flex gap-2">
            <button onClick={handleSave} className="bg-green-600 text-white px-4 py-2 rounded font-semibold">Save</button>
            <button onClick={handleCancel} className="bg-gray-300 text-gray-800 px-4 py-2 rounded font-semibold">Cancel</button>
          </div>
        ) : (
          <button onClick={handleEdit} className="bg-indigo-600 text-white px-4 py-2 rounded font-semibold">Edit</button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4 mb-2">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">First Name</label>
          <input name="first_name" value={form.first_name || ''} onChange={handleChange} disabled={!editMode} className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name</label>
          <input name="last_name" value={form.last_name || ''} onChange={handleChange} disabled={!editMode} className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Employee Email</label>
          <input value={user.email || ''} disabled className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Employee Code</label>
          <input value={e.emp_code || ''} disabled className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Reporting Manager</label>
          <input value={e.reporting_manager_name || ''} disabled className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Date of Birth</label>
          <input type="date" name="date_of_birth" value={form.date_of_birth || ''} onChange={handleChange} disabled={!editMode} className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Father's Name</label>
          <input name="father_name" value={form.father_name || ''} onChange={handleChange} disabled={!editMode} className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Mother's Name</label>
          <input name="mother_name" value={form.mother_name || ''} onChange={handleChange} disabled={!editMode} className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
          <input name="phone_number" value={form.phone_number || ''} onChange={handleChange} disabled={!editMode} className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Adhaar Number</label>
          <input name="adhaar_number" value={form.adhaar_number || ''} onChange={handleChange} disabled={!editMode} className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">PAN Number</label>
          <input name="pan_number" value={form.pan_number || ''} onChange={handleChange} disabled={!editMode} className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Emergency Number</label>
          <input name="emergency_number" value={form.emergency_number || ''} onChange={handleChange} disabled={!editMode} className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Joining Date</label>
          <input value={e.joining_date || ''} disabled className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">User Role</label>
          <input value={user.role_name || ''} disabled className="w-full px-2 py-1 border rounded bg-white disabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" />
        </div>
      </div>
    

      {/* EDUCATION SECTION */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-bold">Education</h3>
          <button onClick={() => setShowEduModal(true)} className="bg-indigo-600 text-white px-3 py-1 rounded text-sm">Add Education</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded">
            <thead>
              <tr className="bg-slate-100 text-xs">
                <th className="px-3 py-2 border">Type</th>
                <th className="px-3 py-2 border">College Name</th>
                <th className="px-3 py-2 border">Location</th>
                <th className="px-3 py-2 border">Start Year</th>
                <th className="px-3 py-2 border">End Year</th>
                <th className="px-3 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {user.educations && user.educations.length > 0 ? user.educations.map(edu => (
                <tr key={edu.id} className="text-sm">
                  <td className="border px-3 py-2">{edu.education_type}</td>
                  <td className="border px-3 py-2">{edu.college_name}</td>
                  <td className="border px-3 py-2">{edu.college_location}</td>
                  <td className="border px-3 py-2">{edu.start_year}</td>
                  <td className="border px-3 py-2">{edu.end_year}</td>
                  <td className="border px-3 py-2">
                    <button onClick={() => handleDeleteEducation(edu.id)} className="text-red-600 hover:underline text-xs">Delete</button>
                  </td>
                </tr>
              )) : <tr><td colSpan="6" className="text-center text-gray-400 py-2">No education added</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {/* ATTACHMENT SECTION */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-bold">Attachments</h3>
          <button onClick={() => setShowAttachModal(true)} className="bg-indigo-600 text-white px-3 py-1 rounded text-sm">Add Attachment</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded">
            <thead>
              <tr className="bg-slate-100 text-xs">
                <th className="px-3 py-2 border text-center">View</th>
                <th className="px-3 py-2 border">Type</th>
                <th className="px-3 py-2 border">File Name</th>
                <th className="px-3 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {user.attachments && user.attachments.length > 0 ? user.attachments.map(att => (
                <tr key={att.id} className="text-sm">
                  <td className="border px-3 py-2 text-center"><button onClick={() => window.open(att.url, '_blank')} className="text-blue-600 hover:underline text-xs mr-2" title="View">
                      <i className="fas fa-eye" />
                    </button></td>
                  <td className="border px-3 py-2">{att.document_type}</td>
                  <td className="border px-3 py-2">{att.file_name}</td>
                  <td className="border px-3 py-2">
                    <button onClick={() => handleDeleteAttachment(att.id)} className="text-red-600 hover:underline text-xs">Delete</button>
                  </td>
                </tr>
              )) : <tr><td colSpan="3" className="text-center text-gray-400 py-2">No attachments added</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* ASSET SECTION */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-bold">Assets</h3>
          <button onClick={() => setShowAssetModal(true)} className="bg-indigo-600 text-white px-3 py-1 rounded text-sm">Add Asset</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded">
            <thead>
              <tr className="bg-slate-100 text-xs">
                <th className="px-3 py-2 border">Asset Name</th>
                <th className="px-3 py-2 border">Serial No./Model</th>
                <th className="px-3 py-2 border">Allotted Date</th>
                <th className="px-3 py-2 border">Surrender Date</th>
                <th className="px-3 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {user.assets && user.assets.length > 0 ? user.assets.map(asset => (
                <tr key={asset.id} className="text-sm">
                  <td className="border px-3 py-2">{asset.asset_name}</td>
                  <td className="border px-3 py-2">{asset.serial_no}</td>
                  <td className="border px-3 py-2">{asset.allotted_date}</td>
                  <td className="border px-3 py-2">{asset.surrender_date}</td>
                  <td className="border px-3 py-2">
                    <div className="flex gap-2">
                      <button onClick={() => {
                        setEditingAsset(asset);
                        setShowAssetModal(true);
                      }} className="text-blue-600 hover:underline text-xs">Edit</button>
                      <button onClick={() => handleDeleteAsset(asset.id)} className="text-red-600 hover:underline text-xs">Delete</button>
                    </div>
                  </td>
                </tr>
              )) : <tr><td colSpan="5" className="text-center text-gray-400 py-2">No assets added</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODALS */}
      {showEduModal && (
        <EducationModal onClose={() => setShowEduModal(false)} onSave={handleAddEducation} />
      )}
      {showAttachModal && (
        <AttachmentModal onClose={() => setShowAttachModal(false)} onSave={handleAddAttachment} />
      )}
      {showAssetModal && (
        <AssetModal 
          onClose={() => {
            setShowAssetModal(false);
            setEditingAsset(null);
          }} 
          onSave={editingAsset ? handleEditAsset : handleAddAsset}
          editingAsset={editingAsset}
        />
      )}

      {/* Add Alert component */}
      <Alert
        isOpen={alertConfig.isOpen}
        onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
        onConfirm={alertConfig.onConfirm}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
      />

      <div className="mt-8 border-t border-gray-200 pt-8">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Resignation</h3>
            <p className="mt-1 text-sm text-gray-500">
              {resignationStatus === 'EXITED' 
                ? 'You have completed the exit process'
                : resignationStatus === 'PENDING' 
                ? 'Your resignation request is pending approval'
                : resignationStatus === 'APPROVED'
                ? 'Your resignation has been approved'
                : resignationStatus === 'REJECTED'
                ? 'Your resignation request was rejected'
                : 'Submit your resignation request'}
            </p>
          </div>
          <div className="flex gap-4">
            {!resignationStatus && (
              <button
                onClick={() => setIsResignationModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Submit Resignation
              </button>
            )}
            {resignationStatus === 'APPROVED' && (
              <button
                onClick={handleExitForm}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Complete Exit Form
              </button>
            )}
          </div>
        </div>
      </div>

      <ResignationFormModal
        isOpen={isResignationModalOpen}
        onClose={() => setIsResignationModalOpen(false)}
        onSubmit={handleResignation}
      />
      <ExitForm
        isOpen={isExitFormOpen}
        onClose={handleCloseExitForm}
      />
    </div>

  );
};

export default Profile;
